import { formatChipLabel, type TimeRange } from "@/components/calendars/time-range-selection.ts";
import { Button } from "@/components/ui/button.tsx";
import { cn } from "@/lib/utils.ts";
import { Calendar, X } from "lucide-react";
import { type RefObject, useCallback, useLayoutEffect, useMemo, useRef, useState } from "react";
import { AgentPopover } from "./AgentPopover.tsx";
import { AgentPromptInput } from "@/components/messages/Omnibox/AgentPromptInput.tsx";

interface RangeSelectionPopoverProps {
  ranges: TimeRange[];
  onDismiss: () => void;
  /** Ref to the calendar area; used as a fallback anchor when a viewport-
   *  filling selection leaves no room beside it. Read in the layout phase
   *  (not the parent's render) so the anchor reference stays stable. */
  parentRef?: RefObject<HTMLDivElement | null>;
}

const SUGGESTION_CHIPS: ReadonlyArray<{ label: string; prefill: string }> = [
  { label: "Create an event", prefill: "Create an event" },
  { label: "Schedule a meeting", prefill: "Schedule a meeting" },
  // The selected time rides along as the range chip already in the composer, so
  // the prefill only has to say what to make of it.
  { label: "Create a reminder", prefill: "Remind me" },
];

/** Union bounding box of every selected-range chip on the grid. */
function unionBoundingRect(nodes: NodeListOf<HTMLElement>): DOMRect | null {
  if (nodes.length === 0) return null;
  let left = Infinity;
  let top = Infinity;
  let right = -Infinity;
  let bottom = -Infinity;
  for (const node of nodes) {
    const r = node.getBoundingClientRect();
    if (r.width === 0 && r.height === 0) continue;
    left = Math.min(left, r.left);
    top = Math.min(top, r.top);
    right = Math.max(right, r.right);
    bottom = Math.max(bottom, r.bottom);
  }
  if (!Number.isFinite(left)) return null;
  return new DOMRect(left, top, right - left, bottom - top);
}

const POPOVER_WIDTH_PX = 400;
const POPOVER_HEIGHT_APPROX_PX = 240;
const POPOVER_GAP_PX = 8;
/** Approximate height of the calendar column-header row; nudges the fallback
 *  anchor below the header so Radix positions the popover in the time grid. */
const CALENDAR_HEADER_HEIGHT_PX = 80;

/**
 * Pick the side that has the most room for the popover. Radix's built-in
 * collision avoidance only flips along one axis (right↔left or top↔bottom)
 * — it can't fall back from right to bottom. In day view the selection
 * spans the whole column width, so neither right nor left fits; we have
 * to choose the right axis up front.
 *
 * Prefers right/left for narrow selections (week/month, where the column
 * is one of many) and bottom/top for wide ones (day view, where the
 * selection takes the full grid width). Among the candidates with enough
 * room, picks the one with the most slack.
 */
function pickBestSide(rect: DOMRect): "top" | "right" | "bottom" | "left" | undefined {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const roomRight = vw - rect.right - POPOVER_GAP_PX;
  const roomLeft = rect.left - POPOVER_GAP_PX;
  const roomBottom = vh - rect.bottom - POPOVER_GAP_PX;
  const roomTop = rect.top - POPOVER_GAP_PX;
  const isWide = rect.width >= POPOVER_WIDTH_PX;

  const candidates = [
    { side: "right" as const, room: roomRight, needs: POPOVER_WIDTH_PX, axis: "horizontal" },
    { side: "left" as const, room: roomLeft, needs: POPOVER_WIDTH_PX, axis: "horizontal" },
    {
      side: "bottom" as const,
      room: roomBottom,
      needs: POPOVER_HEIGHT_APPROX_PX,
      axis: "vertical",
    },
    { side: "top" as const, room: roomTop, needs: POPOVER_HEIGHT_APPROX_PX, axis: "vertical" },
  ]
    .filter((c) => c.room >= c.needs)
    // Wide selections (day view) bias toward vertical sides since the
    // horizontal axis is fully occupied by the column; narrow ones go
    // horizontal first because that's where the empty space lives.
    .sort((a, b) => {
      const aPreferred = isWide ? a.axis === "vertical" : a.axis === "horizontal";
      const bPreferred = isWide ? b.axis === "vertical" : b.axis === "horizontal";
      if (aPreferred !== bPreferred) return aPreferred ? -1 : 1;
      return b.room - a.room;
    });

  return candidates[0]?.side;
}

/**
 * Trigger 2 — opens when the user commits a time-range selection on the grid.
 * Layout, top to bottom:
 *   • Suggestion pills (3 starter prompts that prefill the input)
 *   • AgentPromptInput card — the range chip is passed in as `chipsSlot`
 *     so it renders ABOVE the textarea but INSIDE the bordered shell;
 *     dismissing the chip clears the whole selection
 *
 * Open state derives directly from `ranges.length > 0`. Dismissing the popover
 * clears the selection via the caller; clearing the selection auto-closes the
 * popover. Symmetric.
 */
export const RangeSelectionPopover = ({
  ranges,
  onDismiss,
  parentRef,
}: RangeSelectionPopoverProps) => {
  const [rect, setRect] = useState<DOMRect | null>(null);
  const [side, setSide] = useState<"top" | "right" | "bottom" | "left" | undefined>(undefined);
  // Snapshot of `ranges` that the current `rect` was computed against.
  // When the live `ranges` prop differs (a tap added or removed a chip),
  // the rect is stale — `open` flips to false so the popover unmounts
  // BEFORE the FullCalendar selection chips re-render. Once
  // useLayoutEffect lands the fresh rect + snapshot, the popover remounts
  // at the new position.
  const [rectSnapshot, setRectSnapshot] = useState<TimeRange[]>([]);
  const prefillRef = useRef<((text: string) => void) | null>(null);

  useLayoutEffect(() => {
    if (ranges.length === 0) {
      setRect(null);
      setRectSnapshot(ranges);
      return;
    }
    const nodes = document.querySelectorAll<HTMLElement>(".time-range-selected");
    const selectionRect = unionBoundingRect(nodes);
    if (!selectionRect) {
      setRect(null);
      setRectSnapshot(ranges);
      return;
    }
    const nextSide = pickBestSide(selectionRect);
    setSide(nextSide);

    // When no side has room (a viewport-filling selection like the whole
    // day in day view), the selection bounds are useless as an anchor —
    // Radix would push the popover off-screen. Fall back to the calendar
    // area, nudged below the column header so the popover lands in the
    // visible time grid. Reading the parent rect here (layout phase) keeps
    // the anchor reference stable instead of recomputing on every parent render.
    const parentRect = parentRef?.current?.getBoundingClientRect();
    const anchorRect =
      nextSide || !parentRect
        ? selectionRect
        : new DOMRect(
            parentRect.left,
            parentRect.top + CALENDAR_HEADER_HEIGHT_PX,
            parentRect.width,
            parentRect.height,
          );
    setRect(anchorRect);
    setRectSnapshot(ranges);
  }, [ranges, parentRef]);

  const setInputPrefill = useCallback((text: string) => {
    prefillRef.current?.(text);
  }, []);

  // Virtual anchor derived from the resolved anchor rect (computed in the
  // layout effect above). A new object on every rect change forces Radix to
  // re-measure (the DOM-node approach reused the same element identity and
  // Radix's ResizeObserver wasn't always synchronous, leaving the popover
  // momentarily at a stale position).
  const virtualAnchor = useMemo(() => {
    if (!rect) return null;
    const frozen = rect;
    return { getBoundingClientRect: () => frozen };
  }, [rect]);

  // Open only when the snapshot matches the current ranges — i.e. the
  // rect is known to be in sync with the rendered chips. Mid-transition
  // (snapshot stale), the popover unmounts so it can't render at a stale
  // position or default 0,0.
  const isFresh = rectSnapshot === ranges;
  const open = isFresh && ranges.length > 0 && rect !== null;
  const primaryRange = ranges[0];
  const overflowCount = ranges.length - 1;

  return (
    <AgentPopover
      open={open}
      onOpenChange={(next) => {
        if (!next) onDismiss();
      }}
      anchor={virtualAnchor}
      side={side}
      ariaLabel="Ask the agent about this time"
      // Outside clicks dismiss + clear the selection — but only when
      // they're TRULY outside (page chrome, sidebar, header). Clicks
      // on the calendar grid itself are the user mutating the
      // selection (drag-to-add a new range, tap to add, tap a
      // selected cell to remove); those need to fall through to
      // `useCalendarSelection.toggleTimeRanges` so adds and removes
      // compose with the existing chips instead of clearing them.
      onInteractOutside={(event) => {
        const target = event.target as Element | null;
        // Keep open for: calendar-grid edits (drag/tap to add/remove ranges) and
        // the guided-flow typeahead, which renders in a body portal outside this
        // popover — without sparing it, clicking its contact search would dismiss
        // the whole flow before the input could take focus.
        if (target?.closest?.(".fc-wrapper") || target?.closest?.("[data-typeahead-portal]")) {
          event.preventDefault();
        }
      }}
    >
      <div className="relative flex flex-col gap-2">
        {/* Mouse dismiss — clears the selection and closes the popover (Escape
            and the chip's × do the same; this is the obvious on-popover affordance). */}
        <Button
          type="button"
          variant="ghost"
          size="icon"
          onClick={onDismiss}
          aria-label="Dismiss"
          data-statsig-name="range-popover-dismiss"
          className={cn(
            "absolute right-0 top-0 z-10 size-6 rounded-full",
            "text-muted-foreground hover:bg-secondary hover:text-foreground",
          )}
        >
          <X className="size-4" />
        </Button>
        {/* Floating suggestion pills above the input card — no card
            wrapper, just bare bg-secondary chips (matches Frank's
            `SUGGESTION_CHIPS` row in his SelectionBody). The right padding keeps
            the first row clear of the dismiss ×. */}
        <div className="flex flex-wrap items-center gap-1.5 pr-7">
          {SUGGESTION_CHIPS.map(({ label, prefill }) => (
            <Button
              key={prefill}
              type="button"
              variant="outline"
              size="xs"
              // Trailing space so the caret lands ready for the user to keep
              // typing (e.g. "Create an event |").
              onClick={() => setInputPrefill(`${prefill} `)}
              data-statsig-name="range-popover-suggestion-chip"
              className="h-7 rounded-full px-2.5 text-xs bg-secondary hover:bg-card"
            >
              {label}
            </Button>
          ))}
        </div>
        {/* Range chip + textarea + send all share AgentPromptInput's
            single card — the chip is passed via `chipsSlot` so it
            renders above the textarea but INSIDE the bordered shell. */}
        <AgentPromptInput
          prefillRef={prefillRef}
          onEscape={onDismiss}
          onSubmitted={onDismiss}
          chipsSlot={
            primaryRange && (
              <span
                className={cn(
                  "inline-flex items-center gap-1.5 self-start rounded-full",
                  "border bg-secondary px-3 py-1 text-xs",
                )}
              >
                <Calendar className="h-3 w-3" />
                <span>{formatChipLabel(primaryRange)}</span>
                {overflowCount > 0 && (
                  <span className="text-muted-foreground">+{overflowCount} more</span>
                )}
                <button
                  type="button"
                  onClick={onDismiss}
                  aria-label="Clear time-range selection"
                  data-statsig-name="range-selection-chip-dismiss"
                  className={cn(
                    "ml-0.5 inline-flex h-4 w-4 items-center justify-center rounded",
                    "hover:bg-secondary/80",
                    "focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring",
                  )}
                >
                  <X className="h-3 w-3" />
                </button>
              </span>
            )
          }
        />
      </div>
    </AgentPopover>
  );
};
